import { useEffect, useMemo, type CSSProperties } from 'react';
import { Button } from '@/components/ui/button';
import type { SpinResult } from '@/lib/carousel-service';
import { playFanfare } from '@/lib/carousel-sound';
import { Gift, PartyPopper, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WinCelebrationProps {
  result: SpinResult;
  onContinue: () => void;
}

const CONFETTI_COLORS = ['#f43f5e', '#f59e0b', '#22c55e', '#3b82f6', '#a855f7', '#ec4899', '#14b8a6'];

function ConfettiBurst({ count = 64 }: { count?: number }) {
  const pieces = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => {
        const size = 7 + (i % 6) * 3;
        // Ribbons and squares mixed, so the fall reads less uniform.
        const isRibbon = i % 3 === 0;
        return {
          id: i,
          left: `${(i * 37) % 100}%`,
          delay: `${(i % 16) * 0.14}s`,
          duration: `${2.4 + (i % 7) * 0.28}s`,
          color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
          drift: `${(i % 2 === 0 ? 1 : -1) * (20 + (i % 9) * 12)}px`,
          width: isRibbon ? size * 0.45 : size,
          height: isRibbon ? size * 1.6 : size * 0.65,
          radius: isRibbon ? '2px' : '9999px',
        };
      }),
    [count]
  );

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((piece) => (
        <span
          key={piece.id}
          className="carousel-confetti absolute top-0"
          style={
            {
              left: piece.left,
              width: piece.width,
              height: piece.height,
              borderRadius: piece.radius,
              backgroundColor: piece.color,
              animationDelay: piece.delay,
              animationDuration: piece.duration,
              ['--drift']: piece.drift,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}

export function WinCelebration({ result, onContinue }: WinCelebrationProps) {
  const isBig = result.is_big_prize;

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    playFanfare(isBig);
  }, [isBig]);

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4',
        isBig
          ? 'bg-gradient-to-br from-rose-600 via-amber-500 to-violet-700'
          : 'bg-slate-950/95'
      )}
    >
      {/* Radiating spotlight behind the prize */}
      <div
        className={cn(
          'pointer-events-none absolute left-1/2 top-1/2 h-[70vmin] w-[70vmin] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl',
          isBig ? 'bg-white/25' : 'bg-amber-400/20'
        )}
      />

      {/* Each piece is its own compositor layer, so the count is kept modest
          for the low-power TV this runs on. */}
      <ConfettiBurst count={isBig ? 40 : 20} />

      <div
        className={cn(
          'animate-carousel-pop relative z-10 w-full text-center',
          isBig ? 'max-w-3xl' : 'max-w-lg'
        )}
      >
        <div
          className="animate-carousel-rise mb-3 flex items-center justify-center gap-3 text-white"
          style={{ animationDelay: '0.15s' }}
        >
          {isBig ? (
            <>
              <Sparkles className="h-8 w-8 animate-pulse sm:h-10 sm:w-10" />
              <PartyPopper className="h-11 w-11 animate-bounce sm:h-14 sm:w-14" />
              <Sparkles className="h-8 w-8 animate-pulse sm:h-10 sm:w-10" />
            </>
          ) : (
            <PartyPopper className="h-10 w-10 text-amber-300" />
          )}
        </div>

        {isBig && (
          <p className="animate-carousel-rise mb-1 text-base font-bold uppercase tracking-[0.35em] text-white/90 sm:text-xl">
            Büyük Ödül
          </p>
        )}

        <h2
          className={cn(
            'animate-carousel-rise mb-6 font-black leading-[0.95] drop-shadow-2xl',
            isBig
              ? 'text-5xl text-white sm:text-7xl md:text-8xl'
              : 'bg-gradient-to-b from-white to-amber-200 bg-clip-text text-4xl text-transparent sm:text-6xl'
          )}
          style={{ animationDelay: '0.2s' }}
        >
          KAZANDINIZ!
        </h2>

        <div
          className={cn(
            'animate-carousel-rise mx-auto mb-6 overflow-hidden rounded-3xl bg-white shadow-2xl ring-4',
            isBig
              ? 'animate-carousel-prize-pulse h-60 w-60 ring-white/60 sm:h-80 sm:w-80'
              : 'h-48 w-48 ring-amber-400/50 sm:h-56 sm:w-56'
          )}
          style={{ animationDelay: '0.3s' }}
        >
          {result.image_url ? (
            <img
              src={result.image_url}
              alt={result.prize_name}
              className="h-full w-full object-contain p-4"
            />
          ) : (
            <div className="flex h-full items-center justify-center bg-gradient-to-br from-amber-100 to-rose-100">
              <Gift className={cn(isBig ? 'h-24 w-24' : 'h-16 w-16', 'text-amber-600')} />
            </div>
          )}
        </div>

        <div className="animate-carousel-rise" style={{ animationDelay: '0.4s' }}>
          <p
            className={cn(
              'mb-3 font-black leading-tight text-white drop-shadow',
              isBig ? 'text-3xl sm:text-5xl' : 'text-2xl sm:text-3xl'
            )}
          >
            {result.prize_name}
          </p>
          <p
            className={cn(
              'mx-auto mb-8 inline-flex rounded-full px-4 py-1.5 text-sm font-medium',
              isBig ? 'bg-white/20 text-white' : 'bg-white/10 text-slate-200'
            )}
          >
            {result.customer_first_name} {result.customer_last_name}
          </p>
        </div>

        <div className="animate-carousel-rise" style={{ animationDelay: '0.5s' }}>
          <Button
            size="lg"
            onClick={onContinue}
            className={cn(
              'h-14 rounded-2xl px-10 text-lg font-bold shadow-xl transition-transform active:scale-95',
              isBig
                ? 'bg-white text-rose-700 hover:bg-white/90'
                : 'bg-gradient-to-r from-amber-300 to-amber-500 text-slate-900 hover:from-amber-200 hover:to-amber-400'
            )}
          >
            Devam Et
          </Button>
        </div>
      </div>
    </div>
  );
}
